<template>
  <section>
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <p class="control" style="padding-top: 0.5rem;">
              <span class="subtitle">
                {{ $t('attributes.gift_transfer_management') }}
              </span>
            </p>
          </b-field>
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.gift_stock_type') }}
              </span>
            </p>
            <b-select v-model="filterStockType" @input="onFilter">
              <option value="">{{ $t('attributes.all') }}</option>
              <option
                v-for="option in stockTypes"
                :key="option"
                :value="option"
              >
                {{ $t(`attributes.gift_stock_type_${option}`) }}
              </option>
            </b-select>
          </b-field>
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ $t('attributes.gift_modify_type') }}
              </span>
            </p>
            <b-select v-model="filterModifyType" @input="onFilter">
              <option value="">{{ $t('attributes.all') }}</option>
              <option
                v-for="option in modifyTypes"
                :key="option"
                :value="option"
              >
                {{ $t(`attributes.gift_modify_type_${option}`) }}
              </option>
            </b-select>
          </b-field>
        </b-field>
      </div>

      <div class="column is-3 is-2-desktop">
        <div class="buttons is-right">
          <b-button
            :loading="sending"
            class="is-primary"
            @click="openTransferImport"
          >
            {{ $t('attributes.gift_transfer_in') }}
          </b-button>
          <b-button
            v-if="isAdmin"
            :loading="sending"
            class="is-warning"
            @click="openAdjust"
          >
            {{ $t('attributes.gift_adjust') }}
          </b-button>
        </div>
      </div>
    </nav>

    <b-table
      :data="items"
      :loading="loadingItems"
      :mobile-cards="false"
      scrollable
      hoverable
      narrowed
      paginated
      pagination-position="bottom"
      backend-pagination
      backend-sorting
      height="360"
      sticky-header
      :row-class="rowClass"
      :total="total"
      :current-page="page"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="id"
        :visible="false"
        :label="$t('attributes.id')"
        width="75"
        sortable
        centered
        v-slot="props"
      >
        {{ props.row.id }}
      </b-table-column>

      <b-table-column
        field="gift_name"
        :label="$t('attributes.gift_name')"
        width="120"
        sortable
        header-class="nowrap"
        cell-class="nowrap"
        v-slot="props"
      >
        {{ props.row.gift.full_name }}
      </b-table-column>

      <b-table-column
        field="date"
        :label="$t('attributes.gift_transfer_date')"
        width="120"
        centered
        sortable
        header-class="nowrap"
        cell-class="nowrap"
        v-slot="props"
      >
        {{ formatDate(props.row.date) }}
      </b-table-column>

      <b-table-column
        field="modify_type"
        :label="$t('attributes.gift_modify_type')"
        width="80"
        centered
        header-class="nowrap"
        cell-class="nowrap"
        v-slot="props"
      >
        {{ modifyType(props.row) }}
      </b-table-column>

      <b-table-column
        field="quantity"
        :label="$t('attributes.gift_transfer_quantity')"
        width="90"
        numeric
        header-class="nowrap"
        v-slot="props"
      >
        {{ props.row.quantity }}
      </b-table-column>

      <b-table-column
        field="origin_quantity"
        label="盤前庫存"
        width="90"
        numeric
        header-class="nowrap"
        v-slot="props"
      >
        {{ props.row.origin_quantity }}
      </b-table-column>

      <b-table-column
        field="final_quantity"
        label="盤後庫存"
        width="90"
        numeric
        header-class="nowrap"
        v-slot="props"
      >
        {{ props.row.final_quantity }}
      </b-table-column>

      <b-table-column
        field="final_quantity"
        label="公司庫存"
        width="90"
        numeric
        header-class="nowrap"
        v-slot="props"
      >
        {{ props.row.company_quantity }}
      </b-table-column>

      <b-table-column
        field="stock_type"
        :label="$t('attributes.gift_stock_type')"
        width="80"
        centered
        header-class="nowrap"
        v-slot="props"
      >
        <p class="nowrap">
          {{ stockType(props.row) }}
        </p>
      </b-table-column>

      <b-table-column
        field="employee_code"
        :label="$t('attributes.gift_updater')"
        width="100"
        sortable
        header-class="nowrap"
        cell-class="nowrap"
        v-slot="props"
      >
        {{
          !!props.row.employee_code
            ? `[${props.row.employee_code}] ${props.row.employee_name}`
            : '無'
        }}
      </b-table-column>

      <b-table-column
        field="updated_at"
        :label="$t('attributes.gift_updated_at')"
        width="160"
        centered
        sortable
        header-class="nowrap"
        v-slot="props"
      >
        {{ formatDateTime(props.row.updated_at) }}
      </b-table-column>

      <b-table-column
        field="remark"
        :label="$t('attributes.remark')"
        width="240"
        header-class="nowrap"
        v-slot="props"
      >
        {{ props.row.remark }}
      </b-table-column>

      <b-table-column
        :visible="false"
        :width="240"
        label="功能"
        centered
        header-class="nowrap"
        v-slot="props"
      >
        <div class="buttons is-centered">
          <b-button
            type="is-second"
            size="is-small"
            :disabled="!canModify(props.row)"
            @click.prevent="onModify(props.row)"
          >
            {{ $t('attributes.modify') }}
          </b-button>
        </div>
      </b-table-column>

      <template #empty>
        <section class="section">
          <div
            v-if="!loadingItems"
            class="content has-text-grey has-text-centered"
          >
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>
    <b-modal
      :active.sync="isFormModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
    >
      <gift-transfer-import-form
        :gift-transfer="selected"
        :sending="sending"
        @send="onTransferImport"
      />
    </b-modal>

    <b-modal
      :active.sync="isAdjustFormModalActive"
      :can-cancel="['escape', 'x']"
      has-modal-card
    >
      <gift-transfer-adjust-form :sending="sending" @send="onTransferAdjust" />
    </b-modal>
  </section>
</template>

<script>
import debounce from 'lodash/debounce'
import { GIFT_TRANSFERS_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'
import GiftTransferImportForm from './GiftTransferImportForm'
import GiftTransferAdjustForm from './GiftTransferAdjustForm'

export default {
  name: 'GiftTransferList',
  components: { GiftTransferImportForm, GiftTransferAdjustForm },

  props: {
    consumable: {
      type: Boolean
    },
    search: {
      type: String
    },
    gifts: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      isAdmin:
        [462, 26, 567].indexOf(parseInt(this.$store.getters.empCode)) !== -1,
      loadingItems: false,
      total: 0,
      items: [],
      selected: null,

      stockTypes: ['company', 'vendor'],
      modifyTypes: ['init', 'purchase', 'export', 'import', 'void', 'adjust'],

      filterStockType: '',
      filterModifyType: '',
      sortField: 'updated_at',
      sortOrder: 'desc',
      defaultSortOrder: 'desc',
      page: 1,
      perPage: 10,

      isFormModalActive: false,
      isAdjustFormModalActive: false,
      sending: false
    }
  },
  watch: {
    gifts(items) {
      this.loadItems(this.consumable, items)
    },
    consumable(consumable) {
      this.loadItems(consumable, this.gifts)
    }
  },
  mounted() {
    this.loadItems(this.consumable, this.gifts)
  },
  methods: {
    async loadItems(consumable, items) {
      try {
        const api = `${GIFT_TRANSFERS_API}`
        const params = {
          consumable: consumable,
          gifts: items.map(g => g.id).join(','),
          stock_type: this.filterStockType,
          modify_type: this.filterModifyType,
          search: this.search,
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc',
          page_size: this.perPage,
          page_number: this.page
        }

        this.loadingItems = true
        const res = await this.axios.get(api, { params })
        const data = res.data.data

        this.items = data.items
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('gift_transfers', e)
      } finally {
        this.loadingItems = false
      }
    },
    onFilter: debounce(function() {
      this.page = 1
      this.loadItems(this.consumable, this.gifts)
    }, 500),
    onPageChange(page) {
      this.page = page
      this.loadItems(this.consumable, this.gifts)
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order
      this.loadItems(this.consumable, this.gifts)
    },
    openTransferImport() {
      this.selected = {
        id: null,
        gift_id: null,
        gift_name: null,
        date: null,
        quantity: 1,
        origin_quantity: 0,
        final_quantity: 0,
        unit_cost: null
      }
      this.isFormModalActive = true
    },
    openAdjust() {
      this.isAdjustFormModalActive = true
    },
    onModify(transfer) {
      // eslint-disable-next-line no-console
      console.log('onModify', transfer)
    },
    async onTransferImport(item) {
      try {
        const api = `${GIFT_TRANSFERS_API}/import`
        this.sending = true
        await this.axios.post(api, item)
        notifyService.success(this.$t('attributes.add_succeed'))

        this.$emit('updated')
        await this.loadItems(this.consumable, this.gifts)
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
        this.isFormModalActive = false
      }
    },
    async onTransferAdjust(item) {
      try {
        const api = `${GIFT_TRANSFERS_API}/adjust`
        this.sending = true
        await this.axios.post(api, item)
        notifyService.success(this.$t('attributes.add_succeed'))

        this.$emit('updated')
        await this.loadItems(this.consumable, this.gifts)
      } catch (e) {
        notifyService.create_error(e)
      } finally {
        this.sending = false
        this.isAdjustFormModalActive = false
      }
    },
    canModify(transfer) {
      return (
        transfer.modify_type === 'export' ||
        transfer.modify_type === 'import' ||
        transfer.modify_type === 'adjust'
      )
    },
    rowClass(transfer) {
      switch (transfer.stock_type) {
        case 'company':
          return 'has-background-info-light'
        case 'vendor':
          return 'has-background-warning-light'
      }

      return ''
    },
    stockType(transfer) {
      switch (transfer.stock_type) {
        case 'company':
          return this.$t('attributes.gift_stock_type_company')
        case 'vendor':
          return this.$t('attributes.gift_stock_type_vendor')
      }

      return ''
    },
    modifyType(transfer) {
      switch (transfer.modify_type) {
        case 'init':
          return this.$t('attributes.gift_modify_type_init')
        case 'purchase':
          return this.$t('attributes.gift_modify_type_purchase')
        case 'export':
          return this.$t('attributes.gift_modify_type_export')
        case 'import':
          return this.$t('attributes.gift_modify_type_import')
        case 'void':
          return this.$t('attributes.gift_modify_type_void')
        case 'adjust':
          return this.$t('attributes.gift_modify_type_adjust')
      }

      return ''
    }
  }
}
</script>

<style lang="scss" scoped>
.b-table {
  margin-top: 0.75rem;

  td {
    vertical-align: middle;
  }
}
</style>
